import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Percent, Save, Loader2, ShieldCheck, Receipt, Info } from 'lucide-react';
import { Alert, AlertProps } from '../../Shared/Utils/Alert';
import { TaxConfigRequest, TaxConfigResponse, TaxMode } from '../Types/TaxConfigType';
import { getTaxConfig, saveTaxConfig } from '../Services/TaxConfigService';

const fieldClass =
  'w-full rounded-xl border border-slate-800 bg-slate-950 px-3.5 py-2.5 text-sm text-slate-200 placeholder-slate-600 outline-none transition [color-scheme:dark] focus:border-cyan-500/50';

const labelClass = 'mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-400';

const SAMPLE_AMOUNT = 1000;

type FormState = {
  isEnabled: boolean;
  taxMode: TaxMode;
  cgstPercent: string;
  sgstPercent: string;
  igstPercent: string;
  gstin: string;
  hsnCode: string;
};

const toForm = (config: TaxConfigResponse): FormState => ({
  isEnabled: Boolean(config.isEnabled),
  taxMode: config.taxMode ?? 'CGST_SGST',
  cgstPercent: String(config.cgstPercent ?? 0),
  sgstPercent: String(config.sgstPercent ?? 0),
  igstPercent: String(config.igstPercent ?? 0),
  gstin: config.gstin ?? '',
  hsnCode: config.hsnCode ?? '',
});

const num = (value: string): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Mirrors the server's rounding so the preview cannot lie.
 *
 * <p>Half-up to two decimals, per component, summed - the same sequence {@code TaxCalculator}
 * performs. Duplicating it here is the one risk of previewing in the browser, and it is bounded: this
 * is an illustrative figure for the admin, whereas the charged amount is always recomputed server
 * side and is never read from anything on this screen.
 */
const previewTax = (subtotal: number, mode: TaxMode, cgst: number, sgst: number, igst: number) => {
  const round = (value: number) => Math.round(value * 100) / 100;
  const component = (rate: number) => round((subtotal * rate) / 100);

  const parts =
    mode === 'CGST_SGST'
      ? [component(cgst), component(sgst)]
      : mode === 'IGST'
      ? [component(igst)]
      : [];

  const totalTax = round(parts.reduce((sum, value) => sum + value, 0));

  return { parts, totalTax, grandTotal: round(subtotal + totalTax) };
};

const TaxConfigComponent: React.FC = () => {
  const [form, setForm] = useState<FormState | null>(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState<AlertProps | null>(null);

  const notify = (next: AlertProps) => setAlert({ ...next, onClose: () => setAlert(null) });

  const load = useCallback(async () => {
    try {
      const response = await getTaxConfig();
      if (response?.success && response.data) {
        setForm(toForm(response.data));
      } else {
        notify({
          variant: 'error',
          title: 'Tax configuration',
          message: response?.message || 'Unable to load the tax configuration.',
        });
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const preview = useMemo(() => {
    if (!form) return null;

    // Always computed, even while tax is switched off. Hiding the arithmetic until the switch is
    // on means typing a rate produces no visible feedback at all, which reads as the field having
    // rejected the input rather than as the switch being off.
    const { parts, totalTax, grandTotal } = previewTax(
      SAMPLE_AMOUNT,
      form.taxMode,
      num(form.cgstPercent),
      num(form.sgstPercent),
      num(form.igstPercent)
    );

    return {
      applied: form.isEnabled && form.taxMode !== 'NONE',
      hasRates: parts.some((value) => value > 0),
      parts,
      totalTax,
      grandTotal,
    };
  }, [form]);

  if (!loading && !form) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-800 bg-slate-900/40 p-10 text-center">
        <p className="text-sm text-slate-400">Tax configuration could not be loaded.</p>
      </div>
    );
  }

  if (loading || !form || !preview) {
    return (
      <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-10 text-center text-sm text-slate-400">
        Loading tax configuration...
      </div>
    );
  }

  const handleSave = async () => {
    const payload: TaxConfigRequest = {
      isEnabled: form.isEnabled,
      taxMode: form.taxMode,
      cgstPercent: num(form.cgstPercent),
      sgstPercent: num(form.sgstPercent),
      igstPercent: num(form.igstPercent),
      gstin: form.gstin.trim(),
      hsnCode: form.hsnCode.trim(),
    };

    setSaving(true);
    try {
      const response = await saveTaxConfig(payload);
      if (response?.success) {
        setForm(toForm(response.data));
        notify({
          variant: 'success',
          title: 'Saved',
          message: 'Tax configuration updated. It applies to payments created from now on.',
        });
        return;
      }
      notify({
        variant: 'error',
        title: 'Tax configuration',
        message: response?.message || 'Unable to save the tax configuration.',
      });
    } catch (error: unknown) {
      notify({
        variant: 'error',
        title: 'Tax configuration',
        message: error instanceof Error ? error.message : 'Unable to save the tax configuration.',
      });
    } finally {
      setSaving(false);
    }
  };

  const inState = form.taxMode === 'CGST_SGST';

  return (
    <div className="space-y-6">
      {alert && <Alert {...alert} onClose={() => setAlert(null)} />}

      <div className="rounded-2xl border border-amber-500/20 bg-amber-500/5 p-4 flex items-start gap-3">
        <ShieldCheck className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
        <p className="text-xs text-amber-200/80 leading-relaxed">
          Tax is added on top of the plan price, so every new payment is charged the total shown at
          checkout. Changing a rate here affects payments created from now on - payments already made
          keep the rate they were charged at, because each one stores its own breakdown for the
          receipt.
        </p>
      </div>

      <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 space-y-6">
        {/* Master switch */}
        <div className="flex flex-wrap items-start justify-between gap-4 pb-5 border-b border-slate-800">
          <div>
            <h3 className="text-lg font-black text-white flex items-center gap-2">
              <Percent className="w-5 h-5 text-cyan-400" />
              GST
            </h3>
            <p className="text-xs text-slate-500 font-mono mt-0.5">Applied to every plan</p>
          </div>

          <label className="flex items-center gap-3 cursor-pointer">
            <span className="text-[11px] font-black uppercase tracking-widest text-slate-400">
              {form.isEnabled ? 'Enabled' : 'Disabled'}
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={form.isEnabled}
              disabled={saving}
              onClick={() => set('isEnabled', !form.isEnabled)}
              className={`relative h-6 w-11 rounded-full transition-colors ${
                form.isEnabled ? 'bg-emerald-500' : 'bg-slate-700'
              } disabled:opacity-50`}
            >
              <span
                className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${
                  form.isEnabled ? 'translate-x-[22px]' : 'translate-x-0.5'
                }`}
              />
            </button>
          </label>
        </div>

        {/* Mode */}
        <div>
          <label className={labelClass}>Tax type</label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {(
              [
                { key: 'CGST_SGST', label: 'CGST + SGST', hint: 'Intra-state supply' },
                { key: 'IGST', label: 'IGST', hint: 'Inter-state supply' },
                { key: 'NONE', label: 'No tax', hint: 'Zero-rated or exempt' },
              ] as { key: TaxMode; label: string; hint: string }[]
            ).map((option) => (
              <button
                key={option.key}
                type="button"
                disabled={saving}
                onClick={() => set('taxMode', option.key)}
                className={`rounded-xl border px-4 py-3 text-left transition disabled:opacity-50 ${
                  form.taxMode === option.key
                    ? 'border-cyan-500/50 bg-cyan-500/10'
                    : 'border-slate-800 bg-slate-950 hover:border-slate-700'
                }`}
              >
                <p
                  className={`text-sm font-bold ${
                    form.taxMode === option.key ? 'text-cyan-300' : 'text-slate-200'
                  }`}
                >
                  {option.label}
                </p>
                <p className="text-[11px] text-slate-500 mt-0.5">{option.hint}</p>
              </button>
            ))}
          </div>
          <p className="mt-1.5 text-[11px] text-slate-500">
            CGST+SGST and IGST are alternatives, never a combination - a sale is either within one
            state or between two.
          </p>
        </div>

        {/* Rates. Editable whether or not tax is switched on - the master switch decides whether
            these rates are *applied*, not whether they can be typed. Requiring the switch first made
            the common case impossible to fill in at all, because a fresh install ships with tax
            disabled. */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {inState ? (
            <>
              <div>
                <label className={labelClass}>CGST %</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.0001"
                  value={form.cgstPercent}
                  disabled={saving}
                  onChange={(e) => set('cgstPercent', e.target.value)}
                  className={fieldClass}
                />
              </div>
              <div>
                <label className={labelClass}>SGST %</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.0001"
                  value={form.sgstPercent}
                  disabled={saving}
                  onChange={(e) => set('sgstPercent', e.target.value)}
                  className={fieldClass}
                />
              </div>
            </>
          ) : form.taxMode === 'IGST' ? (
            <div>
              <label className={labelClass}>IGST %</label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.0001"
                value={form.igstPercent}
                disabled={saving}
                onChange={(e) => set('igstPercent', e.target.value)}
                className={fieldClass}
              />
            </div>
          ) : (
            <p className="text-xs text-slate-500 md:col-span-2">
              No rate fields are shown because &ldquo;No tax&rdquo; is selected. Pick CGST+SGST or
              IGST above to enter rates.
            </p>
          )}
        </div>

        {/* Invoice identity */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-5 border-t border-slate-800">
          <div>
            <label className={labelClass}>GSTIN</label>
            <input
              value={form.gstin}
              disabled={saving}
              onChange={(e) => set('gstin', e.target.value)}
              placeholder="e.g. 29ABCDE1234F1Z5"
              className={fieldClass}
            />
            <p className="mt-1.5 text-[11px] text-slate-500">Printed on the receipt.</p>
          </div>
          <div>
            <label className={labelClass}>HSN / SAC code</label>
            <input
              value={form.hsnCode}
              disabled={saving}
              onChange={(e) => set('hsnCode', e.target.value)}
              placeholder="e.g. 998311"
              className={fieldClass}
            />
            <p className="mt-1.5 text-[11px] text-slate-500">Printed on the receipt.</p>
          </div>
        </div>
      </div>

      {/* Worked example */}
      <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6">
        <h4 className="text-[11px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-2">
          <Receipt className="w-4 h-4" />
          Worked example on a {`₹${SAMPLE_AMOUNT.toLocaleString('en-IN')}`} plan
        </h4>

        {!preview.hasRates ? (
          <p className="mt-3 flex items-start gap-2 text-xs text-slate-500">
            <Info className="w-4 h-4 shrink-0 mt-0.5" />
            {form.taxMode === 'NONE'
              ? 'No tax type is selected, so there are no rates to preview. Pick CGST+SGST or IGST above.'
              : 'Enter a rate above and the effect on a plan price appears here.'}
          </p>
        ) : (
          <div className="mt-4 space-y-2 text-sm">
            <div className="flex justify-between text-slate-400">
              <span>Plan price</span>
              <span className="font-mono">{`₹${SAMPLE_AMOUNT.toLocaleString('en-IN')}.00`}</span>
            </div>
            {inState && (
              <>
                <div className="flex justify-between text-slate-400">
                  <span>{`CGST ${form.cgstPercent}%`}</span>
                  <span className="font-mono">{`₹${preview.parts[0]?.toFixed(2) ?? '0.00'}`}</span>
                </div>
                <div className="flex justify-between text-slate-400">
                  <span>{`SGST ${form.sgstPercent}%`}</span>
                  <span className="font-mono">{`₹${preview.parts[1]?.toFixed(2) ?? '0.00'}`}</span>
                </div>
              </>
            )}
            {form.taxMode === 'IGST' && (
              <div className="flex justify-between text-slate-400">
                <span>{`IGST ${form.igstPercent}%`}</span>
                <span className="font-mono">{`₹${preview.parts[0]?.toFixed(2) ?? '0.00'}`}</span>
              </div>
            )}
            <div className="flex justify-between border-t border-slate-800 pt-2 text-slate-200 font-bold">
              <span>Total GST</span>
              <span className="font-mono">{`₹${preview.totalTax.toFixed(2)}`}</span>
            </div>
            <div
              className={`flex justify-between font-black text-base ${
                preview.applied ? 'text-emerald-400' : 'text-slate-500'
              }`}
            >
              <span>{preview.applied ? 'Customer pays' : 'Would pay'}</span>
              <span className="font-mono">{`₹${preview.grandTotal.toLocaleString('en-IN', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}`}</span>
            </div>

            {/* The distinction that matters: the figures above are what tax *would* do, not what a
                customer will be charged. Saying so here is better than an admin saving a rate,
                flipping the switch on later, and being surprised by what it did to live prices. */}
            {!preview.applied && (
              <p className="flex items-start gap-2 pt-2 text-[11px] text-amber-400">
                <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                Preview only - tax is switched off, so customers are not charged this yet.
              </p>
            )}
          </div>
        )}
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          disabled={saving}
          onClick={handleSave}
          className="inline-flex items-center gap-2 rounded-xl border border-cyan-500/30 bg-cyan-500/10 px-5 py-2.5 text-[11px] font-black uppercase tracking-widest text-cyan-300 transition hover:bg-cyan-500/20 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save tax configuration
        </button>
      </div>
    </div>
  );
};

export default TaxConfigComponent;